import dayjs from 'dayjs';
import { rememberedMonth } from './useWarningMonth';

// When a threshold applies - the month-level view of a version row (effective_from / effective_to).
//
// A threshold is saved "from a month onward" or "only this month" (see versioning.shared.js on the backend). A row
// covers the months from the one it starts in up to, but not including, the one it ends in. Everything here works in
// whole months, the way the results pages do.

// The month a new or edited threshold applies from, by default: the month the person is working in (the one picked on
// the dashboard), but never later than this month - scheduling ahead is a deliberate choice, not a default.
export const defaultEffectiveMonth = () => {
  const now = dayjs().startOf('month');
  const picked = rememberedMonth();
  const month = picked ? picked.startOf('month') : now;
  return (month.isAfter(now) ? now : month).format('YYYY-MM');
};

// The form fields every threshold form carries for WHEN it applies (start month, optional last month, "only this month"), sent to the server as-is.
export const emptyEffective = () => ({ effective_month: defaultEffectiveMonth(), effective_until: '', only_this_month: false });

const month = (date) => dayjs(date).startOf('month');
const label = (m) => m.format('MMM YYYY');

// "From Jan 2026", "Oct 2026 only", "Jan 2026 – Sep 2026"
export const windowText = (row) => {
  const from = month(row.effective_from);
  if (!row.effective_to) return `From ${label(from)}`;
  const last = month(row.effective_to).subtract(1, 'month');
  return last.isSame(from, 'month') ? `${label(from)} only` : `${label(from)} – ${label(last)}`;
};

// 'Current' (applies in the working month - by default this month), 'Scheduled' (starts later), 'Ended'
export const versionStatus = (row, now = dayjs()) => {
  const nowMonth = now.startOf('month');
  const from = month(row.effective_from);
  if (from.isAfter(nowMonth)) return 'Scheduled';
  if (row.effective_to && !month(row.effective_to).isAfter(nowMonth)) return 'Ended';
  return 'Current';
};

const newestFirst = (a, b) => {
  const diff = dayjs(b.effective_from).valueOf() - dayjs(a.effective_from).valueOf();
  return diff !== 0 ? diff : Number(b.id) - Number(a.id);
};

// For the single-value screens (one threshold, several versions): the versions oldest first, the one that applies
// this month, and the open-ended one (the one an Edit replaces).
export const splitVersions = (rows, now = dayjs()) => {
  const sorted = [...rows].sort((a, b) => -newestFirst(a, b));
  const current = [...rows].filter((r) => versionStatus(r, now) === 'Current').sort(newestFirst)[0] || null;
  const open = [...rows].filter((r) => !r.effective_to).sort(newestFirst)[0] || null;
  return { sorted, current, open };
};

// "The same time" when ADDING: an active version that BEGINS inside the months the form would cover (from the start month
// up to the last month if there is one). One that began earlier is not a conflict - it is cut back where the new one
// starts. Mirrors conflictWindowSql on the server, which has the final say.
export const startsWithin = (row, form) => {
  const startedIn = String(row.effective_from).slice(0, 7);
  const last = form.only_this_month ? form.effective_month : form.effective_until;
  if (row.effective_to && String(row.effective_to).slice(0, 7) <= startedIn) return false; // emptied (replaced) version
  return startedIn >= form.effective_month && (!last || startedIn <= last);
};
